import React, { useState } from "react";
import { MoreVertical, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  useAddCallingHolidayMutation,
  useDeleteCallingHolidayMutation,
  useGetCallingHolidaysQuery,
} from "@/store/admin_api";
import type { CallingHoliday, CallingRegionOption } from "@/model/interface/CallingRule";
import { FEDERAL_REGION } from "@/util/callingRules";
import { apiErrorMessage } from "@/util/apiError";
import { formatCalendarDate } from "@/util/date";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface CallingHolidaysSectionProps {
  regions: CallingRegionOption[];
}

export function CallingHolidaysSection({ regions }: CallingHolidaysSectionProps) {
  const { data: holidays = [] } = useGetCallingHolidaysQuery();
  const [addHoliday, { isLoading: isAdding }] = useAddCallingHolidayMutation();
  const [deleteHoliday] = useDeleteCallingHolidayMutation();
  const [region, setRegion] = useState(FEDERAL_REGION);
  const [holidayDate, setHolidayDate] = useState("");
  const [name, setName] = useState("");

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!holidayDate || !name.trim()) {
      return;
    }
    try {
      await addHoliday({ region, holidayDate, name: name.trim() }).unwrap();
      toast.success("Holiday added");
      setHolidayDate("");
      setName("");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not add the holiday"));
    }
  };

  const handleDelete = async (holiday: CallingHoliday) => {
    try {
      await deleteHoliday(holiday.id).unwrap();
      toast.success(`${holiday.name} removed`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not remove the holiday"));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold">Holidays</h3>
        <p className="text-sm text-muted-foreground">
          United States dates are federal holidays. Add a state's own holidays under that state; rules that skip state
          holidays read them.
        </p>
      </div>

      <form onSubmit={(event) => void handleAdd(event)} className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Region</label>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {regions.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Date</label>
          <Input type="date" value={holidayDate} onChange={(event) => setHolidayDate(event.target.value)} className="w-44" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Name</label>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Mardi Gras"
            className="w-64"
          />
        </div>
        <Button type="submit" disabled={isAdding || !holidayDate || !name.trim()}>
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </form>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Holiday</TableHead>
            <TableHead>Region</TableHead>
            <TableHead className="text-right">Options</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!holidays.length && (
            <TableRow>
              <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                No holidays listed
              </TableCell>
            </TableRow>
          )}
          {holidays.map((holiday) => (
            <TableRow key={holiday.id}>
              <TableCell className="text-sm">{formatCalendarDate(holiday.holidayDate)}</TableCell>
              <TableCell className="font-medium">{holiday.name}</TableCell>
              <TableCell className="text-sm">{holiday.regionName}</TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" title="Options">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive" onClick={() => void handleDelete(holiday)}>
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
